<template>
    <div class="w-full">
        <div class="flex items-center space-x-3">
            <div class="flex-1 relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 text-gray-400" />
                </div>
                <input type="text" v-model="basicSearchText"
                    placeholder="Search by Person, Project Title or Description"
                    class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    @input="onBasicSearch" />
            </div>
            <button @click="openAdvancedSearchModal"
                class="flex items-center px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                <font-awesome-icon :icon="['fas', 'sliders-h']" class="h-4 w-4 mr-2" />
                Advanced Search
            </button>
        </div>

        <transition name="modal">
            <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
                    <div
                        class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <font-awesome-icon :icon="['fas', 'search-plus']" class="h-5 w-5 text-white mr-3" />
                                    <h3 class="text-lg font-semibold text-white">Advanced Search</h3>
                                </div>
                                <button @click="closeModal"
                                    class="text-white hover:text-gray-200 focus:outline-none focus:text-gray-200 transition-colors duration-200">
                                    <font-awesome-icon :icon="['fas', 'times']" class="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div class="bg-white px-6 py-6">
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div class="space-y-4">
                                    <h4 class="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                                        General</h4>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
                                        <select v-model="advancedSearch.project_type_code"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200">
                                            <option value="">Any</option>
                                            <option v-for="projectType in projectTypes" :key="projectType.short_code"
                                                :value="projectType.short_code">
                                                {{ projectType.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="space-y-4">
                                    <h4 class="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Date
                                        Range</h4>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">From</label>
                                        <input type="date" v-model="advancedSearch.date_from"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">To</label>
                                        <input type="date" v-model="advancedSearch.date_to"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="bg-gray-50 px-6 py-4 flex justify-between items-center">
                            <div class="flex items-center text-sm text-gray-500">
                                <font-awesome-icon :icon="['fas', 'info-circle']" class="h-4 w-4 mr-2" />
                                Use any combination of fields to refine your search
                            </div>
                            <div class="flex space-x-3">
                                <button @click="clearAdvancedSearch"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200">
                                    Clear All
                                </button>
                                <button @click="applyAdvancedSearch"
                                    class="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                                    Apply Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";

const GET_INNOVATION_PROJECT_TYPES = gql`
  query GetInnovationProjectTypes {
    innovationProjectTypes {
      short_code
      name
      role_type
    }
  }
`;

export default {
    name: "InnovationAdvancedSearch",
    props: {
        initialBasicSearch: {
            type: String,
            default: ""
        },
        initialAdvancedSearch: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ["on-basic-search", "on-advanced-search", "on-clear-search"],
    data() {
        return {
            showModal: false,
            basicSearchText: this.initialBasicSearch,
            projectTypes: [],
            advancedSearch: {
                project_type_code: "",
                date_from: "",
                date_to: "",
                ...this.initialAdvancedSearch
            }
        };
    },
    created() {
        this.fetchProjectTypes();
    },
    watch: {
        initialBasicSearch(newVal) {
            this.basicSearchText = newVal;
        },
        initialAdvancedSearch: {
            handler(newVal) {
                this.advancedSearch = { ...this.advancedSearch, ...newVal };
            },
            deep: true
        }
    },
    methods: {
        openAdvancedSearchModal() {
            this.showModal = true;
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.showModal = false;
            document.body.style.overflow = 'auto';
        },
        onBasicSearch() {
            this.$emit("on-basic-search", this.basicSearchText);
        },
        applyAdvancedSearch() {
            this.$emit("on-advanced-search", this.advancedSearch);
            this.closeModal();
        },
        clearAdvancedSearch() {
            this.advancedSearch = {
                project_type_code: "",
                date_from: "",
                date_to: "",
            };
            this.$emit("on-advanced-search", this.advancedSearch);
        },
        clearAllSearch() {
            this.basicSearchText = "";
            this.clearAdvancedSearch();
            this.$emit("on-basic-search", "");
            this.$emit("on-clear-search");
        },
        async fetchProjectTypes() {
            try {
                const response = await this.$apollo.query({
                    query: GET_INNOVATION_PROJECT_TYPES,
                    fetchPolicy: 'network-only'
                });

                if (response.data && response.data.innovationProjectTypes) {
                    this.projectTypes = response.data.innovationProjectTypes;
                }
            } catch (error) {
                console.error('Failed to fetch project types:', error);
            }
        }
    },
    beforeUnmount() {
        document.body.style.overflow = 'auto';
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .inline-block,
.modal-leave-active .inline-block {
    transition: transform 0.3s ease;
}

.modal-enter-from .inline-block,
.modal-leave-to .inline-block {
    transform: scale(0.95) translateY(-20px);
}
</style>
